import { Button, type ButtonProps } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "@/utils/tagManagerUtils";
import { getAuthUrl } from "@/utils/redirectUtils";

export type Tier = "solo" | "agency" | "studio" | "founder";
export type Billing = "monthly" | "yearly";

interface TrialButtonProps extends Omit<ButtonProps, "onClick"> {
  source: "hero" | "pricing_card" | "final_cta" | "pricing_page_banner";
  tier?: Tier;
  billing?: Billing;
  showArrow?: boolean;
}

// Marketing site hands off to app.upfixe.com which owns the Paddle checkout.
// The app reads ?plan= and ?billing= from the URL to open the right Paddle
// product. No Paddle credentials are needed on the marketing site itself.
const buildSignupUrl = (tier: Tier, billing: Billing, source: string) => {
  const params = new URLSearchParams({
    plan: tier,
    billing,
    utm_source: "marketing_site",
    utm_medium: "cta",
    utm_campaign: source,
  });
  return `${getAuthUrl()}?${params.toString()}`;
};

const TrialButton = ({
  source,
  tier = "solo",
  billing = "monthly",
  showArrow = true,
  children = "Start 7-day free trial — $99/mo after",
  variant = "default",
  size = "lg",
  className,
  ...rest
}: TrialButtonProps) => {
  const handleClick = () => {
    trackEvent("mcp_trial_cta_click", { source, tier, billing });
    window.location.href = buildSignupUrl(tier, billing, source);
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handleClick}
      {...rest}
    >
      {children}
      {showArrow && <ArrowRight className="ml-1 h-4 w-4" />}
    </Button>
  );
};

export default TrialButton;
